import {
  getCustomFieldNames,
  type CustomFieldFilter,
  type CustomFieldProperty,
  type CustomFieldsSchema,
  type CustomFieldValue,
  type CustomFieldValues,
} from "@forgepoint/shared";
import {
  deriveCustomFieldType,
  type CustomFieldType,
} from "./customFieldTypes";

const CUSTOM_FIELD_ID_PREFIX = "custom:";

const FILTERABLE_CUSTOM_FIELD_TYPES = [
  "select",
  "multiSelect",
  "user",
  "multiUser",
] as const;

/** A custom field type that can be filtered by value. */
export type FilterableCustomFieldType =
  (typeof FILTERABLE_CUSTOM_FIELD_TYPES)[number];

/** A custom field offered as a filter, with its derived type and definition. */
export interface FilterableCustomField {
  name: string;
  type: FilterableCustomFieldType;
  property: CustomFieldProperty;
}

/** The id of a custom field in filter keys and card-field selections. */
export type CustomFieldId = `${typeof CUSTOM_FIELD_ID_PREFIX}${string}`;

/**
 * Builds the id for one of the organization's custom fields, used as its
 * filter key and its card-field visibility id.
 */
export function customFieldId(fieldName: string): CustomFieldId {
  return `${CUSTOM_FIELD_ID_PREFIX}${fieldName}`;
}

/** Reads the field name out of a custom field id, or null for any other key. */
export function parseCustomFieldId(key: string): string | null {
  if (!key.startsWith(CUSTOM_FIELD_ID_PREFIX)) {
    return null;
  }
  return key.slice(CUSTOM_FIELD_ID_PREFIX.length);
}

/**
 * Lists the schema's select, multi-select, user, and multi-user fields, in the
 * schema's display order.
 */
export function getFilterableCustomFields(
  schema: CustomFieldsSchema | null | undefined,
): FilterableCustomField[] {
  return getCustomFieldNames(schema).flatMap((name) => {
    const property = schema?.properties[name];
    if (!property) {
      return [];
    }
    const type = deriveCustomFieldType(property);
    return isFilterableType(type) ? [{ name, type, property }] : [];
  });
}

/** Whether a filterable field holds org member ids rather than option values. */
export function isUserCustomField(field: FilterableCustomField): boolean {
  return field.type === "user" || field.type === "multiUser";
}

/**
 * Whether custom field values match every filter entry: a string that is one of
 * the entry's values, or an array sharing an element with them. Client twin of
 * the backend's `buildCustomFieldCondition`.
 */
export function matchesCustomFieldFilters(
  values: CustomFieldValues | null | undefined,
  filter: CustomFieldFilter | undefined,
): boolean {
  return Object.entries(filter ?? {}).every(([name, allowed]) =>
    valueMatches(values?.[name], allowed),
  );
}

/**
 * Returns the filter with one field's selection replaced; an empty selection
 * removes the field, and an empty filter becomes undefined.
 */
export function withCustomFieldSelection(
  filter: CustomFieldFilter | undefined,
  fieldName: string,
  selected: string[],
): CustomFieldFilter | undefined {
  const next = Object.fromEntries(
    Object.entries(filter ?? {}).filter(([name]) => name !== fieldName),
  );
  if (selected.length) {
    next[fieldName] = selected;
  }
  return Object.keys(next).length ? next : undefined;
}

/**
 * Drops filter entries for fields that are not in the filterable list, such as
 * a deleted field or one whose type changed; an empty result becomes undefined.
 */
export function pruneCustomFieldFilter(
  filter: CustomFieldFilter | undefined,
  fields: FilterableCustomField[],
): CustomFieldFilter | undefined {
  const names = new Set(fields.map((field) => field.name));
  const kept = Object.entries(filter ?? {}).filter(([name]) => names.has(name));
  return kept.length ? Object.fromEntries(kept) : undefined;
}

function isFilterableType(
  type: CustomFieldType,
): type is FilterableCustomFieldType {
  return FILTERABLE_CUSTOM_FIELD_TYPES.some(
    (filterable) => filterable === type,
  );
}

function valueMatches(
  value: CustomFieldValue | undefined,
  allowed: string[],
): boolean {
  if (typeof value === "string") {
    return allowed.includes(value);
  }
  if (Array.isArray(value)) {
    return value.some((item) => allowed.includes(item));
  }
  return false;
}
